import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ParallaxHero from '../../components/ui/ParallaxHero';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMail, FiPhone, FiInstagram, FiCalendar } = FiIcons;

const ContactField = ({ label, type = 'text', placeholder, name, value, onChange, required }) => (
  <div className="flex flex-col gap-2">
    <label className="text-xs uppercase tracking-widest text-cream/60 font-semibold">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 px-5 py-4 focus:outline-none focus:border-gold/60 transition-colors duration-500 font-light text-base rounded-sm"
    />
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = e => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div>
      <ParallaxHero
        image="https://media-manager-c.questera.ai/greta-media/8c84f85263efda659889de32f1f32e795be69e9a2504f254db44deecf03d5d2eaf8d8138b6c4ad97d7d004f4d5618742/images/aW1hZ2UvcG5n/5e962f4e81ed25550f0f4cdc2d6861b4.png"
        tag="Dein erster Schritt"
        title="Buche Deinen GlücksCall."
        titleHighlight="GlücksCall."
        subtitle="Ein kostenloses, 30-minütiges Gespräch. Kein Druck – nur echte Verbindung und Klarheit."
      />

      <Section className="bg-dark">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
          {/* Left – Info */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                className="text-gold tracking-[0.2em] uppercase text-sm mb-4">
                Was Dich erwartet
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="text-3xl md:text-4xl font-serif text-cream leading-tight mb-6">
                30 Minuten, die <span className="italic text-gradient-gold">alles verändern</span> können.
              </motion.h2>
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                className="text-cream/65 font-light text-lg leading-relaxed">
                Im GlücksCall schauen wir gemeinsam, wo Du gerade stehst, welche Blockaden Dich aufhalten und welches Programm wirklich zu Dir passt. Völlig unverbindlich, persönlich und ehrlich.
              </motion.p>
            </div>

            {[
              { icon: FiCalendar, title: 'Kostenlos & unverbindlich', desc: '30 Minuten via Zoom – kein Verkaufsgespräch.' },
              { icon: FiMail, title: 'Persönliche Antwort', desc: 'Jede Anfrage wird von Jennifer persönlich gelesen.' },
              { icon: FiPhone, title: 'Sofortige Klarheit', desc: 'Du weißt danach genau, was Dein nächster Schritt ist.' },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="flex items-start gap-5 p-6 glass-panel border-l-2 border-gold/30 hover:border-gold transition-colors duration-500"
                style={{ boxShadow: 'none' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '-4px 0 20px rgba(212,175,55,0.15)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <SafeIcon icon={item.icon} className="text-gold text-xl shrink-0 mt-1" style={{ filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))' }} />
                <div>
                  <p className="font-serif text-cream mb-1">{item.title}</p>
                  <p className="text-cream/55 text-sm font-light">{item.desc}</p>
                </div>
              </motion.div>
            ))}

            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
              className="pt-4 flex gap-4">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"
                className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-cream/50 hover:text-gold hover:border-gold transition-all duration-500">
                <SafeIcon icon={FiInstagram} />
              </a>
              <a href="mailto:hallo@coachingfemale.com"
                className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-cream/50 hover:text-gold hover:border-gold transition-all duration-500">
                <SafeIcon icon={FiMail} />
              </a>
            </motion.div>
          </div>

          {/* Right – Form */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3 glass-panel p-10 md:p-14 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-[2px]"
              style={{ background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }} />

            {submitted ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="text-center py-20 flex flex-col items-center gap-6">
                <div className="w-16 h-16 rounded-full border-2 border-gold flex items-center justify-center text-gold text-2xl"
                  style={{ boxShadow: '0 0 30px rgba(212,175,55,0.3)' }}>
                  ✓
                </div>
                <h3 className="text-3xl font-serif text-cream">Deine Anfrage ist angekommen.</h3>
                <p className="text-cream/65 font-light max-w-md">
                  Jennifer meldet sich persönlich bei Dir. Bereite Dich auf Deinen Durchbruch vor.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="text-2xl font-serif text-cream mb-8">Dein GlücksCall Anfrage</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <ContactField label="Dein Name" name="name" placeholder="Jennifer Gräser" value={form.name} onChange={handleChange} required />
                  <ContactField label="E-Mail" type="email" name="email" placeholder="du@email.com" value={form.email} onChange={handleChange} required />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-widest text-cream/60 font-semibold">Dein Thema</label>
                  <select name="topic" value={form.topic} onChange={handleChange}
                    className="bg-white/5 border border-white/10 text-cream px-5 py-4 focus:outline-none focus:border-gold/60 transition-colors duration-500 font-light text-base rounded-sm">
                    <option value="" className="bg-dark">Wähle Deinen Schwerpunkt...</option>
                    <option value="love" className="bg-dark">The Love Code – Beziehungsheilung</option>
                    <option value="business" className="bg-dark">The Business Code – Female Leadership</option>
                    <option value="both" className="bg-dark">Beides – Ganzheitliche Transformation</option>
                    <option value="other" className="bg-dark">Anderes Thema</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-widest text-cream/60 font-semibold">Deine Situation</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={5}
                    placeholder="Erzähl mir kurz, wo Du gerade stehst und was Du Dir für Dein Leben wünschst..."
                    className="bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 px-5 py-4 focus:outline-none focus:border-gold/60 transition-colors duration-500 font-light text-base rounded-sm resize-none"
                  />
                </div>

                <p className="text-xs text-cream/35 font-light">Mit dem Absenden stimmst Du unserer Datenschutzerklärung zu. Kein Spam, versprochen.</p>

                <Button variant="gold" type="submit" className="w-full">
                  Jetzt GlücksCall anfragen →
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </Section>

      {/* Final Quote */}
      <Section className="bg-darker border-t border-white/5 text-center">
        <motion.blockquote initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="max-w-3xl mx-auto text-3xl md:text-4xl font-serif text-cream italic leading-tight"
          style={{ textShadow: '0 0 60px rgba(212,175,55,0.08)' }}>
          "Die mutigste Entscheidung, die Du treffen kannst, ist die,{' '}
          <span className="text-gradient-gold">Dich selbst zu wählen.</span>"
        </motion.blockquote>
        <p className="text-cream/40 mt-6 tracking-widest uppercase text-sm">— Jennifer Gräser</p>
      </Section>
    </div>
  );
};

export default Contact;